import {
  AsyncSelect,
  type IAsyncSelectProps,
  Space,
  Text,
  TextLabel,
} from '@procurify/ui'
import {
  components as ReactSelectComponents,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { TaxAPI } from '@webapp/api/TaxAPI'
import { useProcurifyIntl, useFormatTaxPercentage } from '@webapp/hooks'
import { type Tax } from '@webapp/models'
import { parseAPIError } from '@webapp/utils/errors'
import {
  useCustomTaxGroupedOptions,
  useCustomTaxOptions,
  useNoTaxOptions,
} from './hooks/useCustomTaxOptions'
import messages from './messages'
import { CustomTaxSelectStyled } from './styles/CustomTaxSelectStyled'
import { isTaxOptionType } from '../DeprecatedOrderItemForm/utils'
import { toast } from '../Notification'

interface ITaxSelectOptionProps extends Tax {
  isLegacy?: boolean
  value?: string
}

const renderOption = ({ name, percentage, isDefault, isLegacy = false }) => {
  const intl = useProcurifyIntl()
  const formatTaxPercentage = useFormatTaxPercentage()

  const formattedTextPercentage = formatTaxPercentage(percentage || 0)

  return (
    <Space direction='horizontal' justify='space-between' size='large'>
      <Space direction='horizontal' size='small' align='center'>
        <Text textEllipsis>{name}</Text>
        {isDefault && !isLegacy && (
          <TextLabel>{intl.formatMessage(messages.defaultTaxLabel)}</TextLabel>
        )}
        {isLegacy && (
          <TextLabel>
            {intl.formatMessage(messages.legacyPresetTaxLabel)}
          </TextLabel>
        )}
      </Space>
      {percentage && <Text>{formattedTextPercentage}</Text>}
    </Space>
  )
}

const components = {
  Option: (props: OptionProps<Tax, false>) => {
    const {
      data: { name, percentage, is_default: isDefault },
    } = props

    return (
      <ReactSelectComponents.Option {...props}>
        {renderOption({ name, percentage, isDefault })}
      </ReactSelectComponents.Option>
    )
  },
  SingleValue: (props: SingleValueProps<ITaxSelectOptionProps, false>) => {
    const {
      data: { name, percentage, value, is_default: isDefault, isLegacy },
    } = props
    const customOptions = [...useCustomTaxOptions(), ...useNoTaxOptions()]
    const isCustomOptions = isTaxOptionType(name, customOptions)

    const renderedOption = renderOption({
      name,
      percentage: isCustomOptions ? null : percentage ?? Number(value) / 100,
      isDefault,
      isLegacy,
    })

    return (
      <ReactSelectComponents.SingleValue {...props}>
        {renderedOption}
      </ReactSelectComponents.SingleValue>
    )
  },
}

export interface ICustomTaxSelectProps
  extends IAsyncSelectProps<ITaxSelectOptionProps> {}

export const CustomTaxSelect = ({ ...props }: ICustomTaxSelectProps) => {
  const intl = useProcurifyIntl()
  const customOptions = useCustomTaxGroupedOptions()

  const loadOptions: IAsyncSelectProps['loadOptions'] = async (
    inputValue = '',
    pagination,
    callback
  ) => {
    try {
      const params = {
        name: inputValue,
        page: pagination.page,
        page_size: 10,
      }
      const res = await TaxAPI.getActiveTaxes(params)
      const groupedOptions = [
        ...customOptions,
        {
          label: intl.formatMessage(messages.presetOptionGroupLabel),
          options: res.data,
        },
      ]
      callback({
        options: groupedOptions,
        pagination: {
          hasMore: !!res.metadata.pagination.next,
          page: pagination.page + 1,
        },
      })
    } catch (err) {
      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children: parseAPIError(err.data).message,
      })
      callback({
        options: customOptions,
        pagination: { hasMore: false },
      })
    }
  }

  return (
    <CustomTaxSelectStyled>
      <AsyncSelect<ITaxSelectOptionProps>
        cacheOptions
        placeholder={intl.formatMessage(messages.selectTaxPlaceholder)}
        loadOptions={loadOptions}
        components={components}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.name}
        {...props}
      />
    </CustomTaxSelectStyled>
  )
}

CustomTaxSelect.displayName = 'CustomTaxSelect'
